/** TanStack Query hooks. All cache keys are namespaced so logout can clear them. */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'

import type { ItemInput, ItemRecord } from '@/domain/types'
import type { SummaryResponse } from './api'
import { disconnectApp, listConnectedApps, type ConnectedApp } from './mcp'
import { callApi, createItem, deleteItem, listItems, updateItem, updateSettings } from './pb'

export const keys = {
  items: ['items'] as const,
  item: (id: string) => ['items', id] as const,
  summary: ['summary'] as const,
  connectedApps: ['connected-apps'] as const,
}

export function useItems(): UseQueryResult<ItemRecord[]> {
  return useQuery({ queryKey: keys.items, queryFn: listItems })
}

export function useSummary(): UseQueryResult<SummaryResponse> {
  return useQuery({
    queryKey: keys.summary,
    queryFn: () => callApi<SummaryResponse>('/api/summary'),
    // Totals are derived from items; a stale figure next to a fresh list looks
    // like a bug, so this is invalidated on every write below.
    staleTime: 30_000,
  })
}

export function useCreateItem() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ input }: { input: ItemInput }) => createItem(input),
    onSuccess: () => invalidateItemData(client),
  })
}

export function useUpdateItem() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<ItemInput> }) =>
      updateItem(id, input),
    onSuccess: () => invalidateItemData(client),
  })
}

export function useDeleteItem() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteItem(id),
    onSuccess: () => invalidateItemData(client),
  })
}

export function useUpdateSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: updateSettings,
    // Digest hour and offsets change when reminders fire, not what is shown.
    onSuccess: () => client.invalidateQueries({ queryKey: keys.summary }),
  })
}

/**
 * Grants made over MCP.
 *
 * `retry: false` because the only likely failure is the collection not existing
 * on an installation that has not run the migration — retrying that four times
 * to still show nothing is pointless, and the card hides itself on error.
 */
export function useConnectedApps(): UseQueryResult<ConnectedApp[]> {
  return useQuery({ queryKey: keys.connectedApps, queryFn: listConnectedApps, retry: false })
}

export function useDisconnectApp() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: disconnectApp,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.connectedApps }),
  })
}

function invalidateItemData(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: keys.items })
  void client.invalidateQueries({ queryKey: keys.summary })
}
